import { useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ImageWithSkeleton from "../ImageWithSkeleton/ImageWithSkeleton";

// Carrusel de fotos para la card de propiedad, estilo Mercado Libre:
// swipe nativo en mobile (scroll-snap) + flechas en desktop + puntitos.
// Vive dentro de un <Link>, por eso las flechas/puntos frenan la navegación.
const MAX_IMAGES = 10;

function CardImageCarousel({ images = [], alt = "" }) {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const imgs = images.slice(0, MAX_IMAGES);

  if (imgs.length <= 1) {
    return (
      <ImageWithSkeleton
        src={imgs[0]}
        alt={alt}
        className="w-full h-60"
        imgClassName="w-full h-full object-cover group-hover:scale-105"
      />
    );
  }

  const handleScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  const goTo = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(imgs.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="relative w-full h-60">
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex w-full h-full overflow-x-auto snap-x snap-mandatory overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {imgs.map((src, i) => (
          <ImageWithSkeleton
            key={i}
            src={src}
            alt={`${alt} - foto ${i + 1}`}
            className="w-full h-full flex-shrink-0 snap-center"
            imgClassName="w-full h-full object-cover"
          />
        ))}
      </div>

      {/* Flechas (solo desktop, aparecen al hacer hover en la card) */}
      {index > 0 && (
        <button
          type="button"
          aria-label="Foto anterior"
          onClick={(e) => goTo(e, index - 1)}
          className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-md opacity-0 group-hover:opacity-100 transition hover:bg-white"
        >
          <FaChevronLeft className="w-3.5 h-3.5" />
        </button>
      )}
      {index < imgs.length - 1 && (
        <button
          type="button"
          aria-label="Foto siguiente"
          onClick={(e) => goTo(e, index + 1)}
          className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-md opacity-0 group-hover:opacity-100 transition hover:bg-white"
        >
          <FaChevronRight className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Puntitos indicadores */}
      <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
        {imgs.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Ir a la foto ${i + 1}`}
            onClick={(e) => goTo(e, i)}
            className={`rounded-full transition-all duration-300 ${
              i === index
                ? "w-2 h-2 bg-white shadow"
                : "w-1.5 h-1.5 bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default CardImageCarousel;
